import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, EDIT, VIEW, DANGER, SUCCESS,
} from "const"
import { MtUser } from "server/models"
import { StateType, hasProjectAccess } from "client/utils"
import {
    PRM_TEAM,
    StoreType,
    AccountUserList,
    ProjectUserList,
    UserFilters,

    LoadAccountUsers,
    LoadProjectUsers,
    LoadProjectGroups,
    LoadAllProjectUserGroups,
    SortUsers,
    DeleteProjectUser,
    GetMaxProjectUsers,

    setAccountUserList,
    setProjectUserList,
    setProjectGroupList,
    setProjectUserGroupList,
    setAssignedProjectUserIds,
    setAssignedProjectUserSort,
    setAssignedProjectUserFilters,
    setNotAssignedProjectUserSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Feedback,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import UserSearch from "client/pages/search/userSearch"
import NotAssignedUsers from "../notAssigned"
import Columns from "./columns"

const ProjectUser = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)

    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const projectUserIds = (projectUserList && projectUserList[project.id]) || []

    const userIds = useSelector((state: StoreType) => state.projectUser.assignedUsers.userIds)
    const sortColumn = useSelector((state: StoreType) => state.projectUser.assignedUsers.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.projectUser.assignedUsers.sortOrder)
    const filters = useSelector((state: StoreType) => state.projectUser.assignedUsers.filters)
    const reloadList = useSelector((state: StoreType) => state.projectUser.reloadProjectUserList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList || !project.id) return
        setLoadState(PENDING)
        Promise.all([
            LoadAccountUsers(project.accountId).then((accountUsers) => {
                const newAccountUserList: AccountUserList = { [project.accountId]: accountUsers }
                dispatch(setAccountUserList(newAccountUserList))
            }),
            LoadProjectUsers(project.id).then((res) => {
                const newProjectUserList: ProjectUserList = { [project.id]: res }
                dispatch(setProjectUserList(newProjectUserList))
            }),
            LoadProjectGroups(project.accountId, project.id, true)
            .then((res1) => {
                dispatch(setProjectGroupList(res1))
                LoadAllProjectUserGroups(project.accountId, project.id, res1)
                .then((res2) => {
                    dispatch(setProjectUserGroupList(res2))
                })
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList, project])

    useEffect(() => {
        const sortedUserIds = SortUsers(Object.values(userList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedUserIds.filter((userId) => projectUserIds.includes(userId))
        dispatch(setAssignedProjectUserIds(sortedAssigned))
    }, [projectUserList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtUser, order) => {
        dispatch(setAssignedProjectUserSort(column, order))
    }

    const onFilter = (newFilters: UserFilters) => {
        dispatch(setAssignedProjectUserFilters(newFilters))
    }

    const onDelete: DeleteRow = (user: MtUser, callback) => {
        DeleteProjectUser(user.id, project.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const newProjectUserList: ProjectUserList = { [project.id]: projectUserIds.filter((id) => id !== user.id) }
                dispatch(setProjectUserList(newProjectUserList))
            })
        })
    }

    const columns = Columns()

    // max number of team members
    const {
        feedback,
        maxNumberReached,
        maxNumberExceeded,
    } = GetMaxProjectUsers(packageSettings, projectUserIds.length, lang)

    const viewAccess = hasProjectAccess(PRM_TEAM, VIEW)
    const editAccess = hasProjectAccess(PRM_TEAM, EDIT)

    if (!viewAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.users}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <UserSearch
                        id="projectUsers"
                        rightOffset={editAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        editAccess && (
                            <Button
                                variant="success"
                                disabled={maxNumberReached}
                                onClick={() => {
                                    dispatch(setNotAssignedProjectUserSelected(null))
                                    dispatch(addModal({
                                        key: "notAssignedUsers",
                                        content: <NotAssignedUsers />,
                                    }))
                                }}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={userList}
                    dataIds={userIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
                <PageForm>
                    <Feedback variant={maxNumberExceeded ? DANGER : SUCCESS}>
                        {feedback}
                    </Feedback>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default ProjectUser
